/**
 * 顧客 API サービス
 *
 * Cloud Functions の顧客 API を呼び出すためのサービスです。
 */

import { getAuthInstance } from "../lib/firebase";
import { CUSTOMER_API_BASE_URL } from "../constants/api";
import { SerializedTimestamp } from "../types/common";
import { FAX_DOMAIN } from "../constants/common";

/**
 * 顧客情報
 */
export interface Customer {
  customerId: string;
  customerName: string;
  replyTo: string[];
  status: "active" | "inactive";
  notes?: string;
  createdAt: SerializedTimestamp;
  updatedAt: SerializedTimestamp;
}

/**
 * FAX転送先アドレスを生成
 */
export function generateFaxAddress(customerId: string): string {
  return `ocr+${customerId}@${FAX_DOMAIN}`;
}

/**
 * 認証ヘッダーを取得
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const auth = getAuthInstance();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("認証されていません");
  }

  const token = await user.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * API エラーをハンドリング
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "不明なエラー" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * 顧客一覧を取得
 */
export async function fetchCustomers(): Promise<Customer[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(CUSTOMER_API_BASE_URL, { headers });
  const data = await handleResponse<{ customers: Customer[] }>(response);
  return data.customers;
}

/**
 * 顧客を作成
 */
export async function createCustomer(
  customerId: string,
  customerName: string,
  notes?: string
): Promise<Customer> {
  const headers = await getAuthHeaders();
  const response = await fetch(CUSTOMER_API_BASE_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({ customerId, customerName, notes, replyTo: [] }),
  });
  return handleResponse<Customer>(response);
}

/**
 * 顧客を更新
 */
export async function updateCustomer(
  customerId: string,
  data: Partial<Pick<Customer, "customerName" | "status" | "notes" | "replyTo">>
): Promise<Customer> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/${customerId}`, {
    method: "PUT",
    headers,
    body: JSON.stringify(data),
  });
  return handleResponse<Customer>(response);
}

/**
 * 顧客を削除
 */
export async function deleteCustomer(customerId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/${customerId}`, {
    method: "DELETE",
    headers,
  });
  await handleResponse<{ success: boolean }>(response);
}
